export interface WithdrawalDTO {
  market_id?: string;
  amount_minor?: number;
  currency?: string;
  currency_symbol?: string;
  currency_exponent?: number;
  id: number;
  user_id: number;
  amount_fen: number;
  amount_yuan: string;
  channel?: 'bank_card' | 'alipay' | 'red_packet' | '';
  masked_account?: string;
  target_summary?: string;
  status: string;
  payout_mode?: 'manual' | 'auto' | '';
  payout_provider?: string;
  payout_status?: '' | 'manual' | 'queued' | 'processing' | 'succeeded' | 'failed';
  payout_out_trade_no?: string;
  provider_trade_no?: string;
  provider_channel_code?: string;
  payout_retry_count?: number;
  payout_last_error_code?: string;
  payout_last_error_message?: string;
  manual_processing?: boolean;
  review_note: string;
  next_action?:
    | 'none'
    | 'review'
    | 'manual_settlement'
    | 'await_auto_payout_create'
    | 'await_auto_payout_result'
    | 'retry_auto_or_switch_manual';
  settlement_note?: string;
  payment_proof_data_url?: string;
  created_at: string;
  updated_at: string;
  reviewer_id?: number;
  settler_id?: number;
  reviewed_at?: string;
  settled_at?: string;
  payout_requested_at?: string;
  payout_finished_at?: string;
  next_retry_at?: string;
  last_query_at?: string;
  can_start_payout?: boolean;
  can_retry_payout?: boolean;
  can_query_payout?: boolean;
  can_switch_manual?: boolean;
  can_manual_settle?: boolean;
  can_reject?: boolean;
  /** Admin list only */
  nickname?: string;
  /** Admin list only */
  email?: string;
  /** Admin list only */
  account_name?: string;
  /** Admin list only */
  bank_name?: string;
  /** Admin list only */
  bank_card_number?: string;
  /** Admin list only */
  alipay_account?: string;
}

export interface PayoutAttemptDTO {
  id: number;
  withdrawal_id: number;
  attempt_no: number;
  action: 'create' | 'query' | 'callback' | string;
  provider: string;
  request_payload?: Record<string, unknown>;
  response_payload?: Record<string, unknown>;
  provider_status?: string;
  success: boolean;
  error_code?: string;
  error_message?: string;
  created_by?: number;
  created_at: string;
}

export interface PayoutJobDTO {
  id: number;
  withdrawal_id: number;
  job_type: 'create' | 'query' | string;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled' | string;
  available_at: string;
  locked_at?: string;
  locked_by?: string;
  attempt_count: number;
  last_error_code?: string;
  last_error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface WithdrawalDetailDTO extends WithdrawalDTO {
  latest_attempts: PayoutAttemptDTO[];
  recent_jobs: PayoutJobDTO[];
}

export interface PayoutReconcileSummaryDTO {
  outstanding_count: number;
  outstanding_amount_fen: number;
  outstanding_amount_yuan: string;
  approved_count: number;
  approved_amount_fen: number;
  approved_amount_yuan: string;
  paying_count: number;
  paying_amount_fen: number;
  paying_amount_yuan: string;
  failed_count: number;
  failed_amount_fen: number;
  failed_amount_yuan: string;
}

export interface PayoutBalanceSnapshotDTO {
  available_fen: number;
  available_yuan: string;
  frozen_fen: number;
  frozen_yuan: string;
  total_fen: number;
  total_yuan: string;
}

export interface PayoutReconcileItemDTO extends WithdrawalDTO {
  reconcile_reason: string;
  reference_at: string;
}

export interface PayoutReconcileReportDTO {
  provider: string;
  generated_at: string;
  stale_before: string;
  limit: number;
  summary: PayoutReconcileSummaryDTO;
  balance?: PayoutBalanceSnapshotDTO | null;
  provider_balance_error?: string;
  items: PayoutReconcileItemDTO[];
}

export interface PayoutReconcileRunResultDTO extends PayoutReconcileReportDTO {
  queued_create_jobs: number;
  queued_processing_jobs: number;
  queued_failed_jobs: number;
}

export interface WithdrawalListResponseDTO {
  pending_total_amount_display?: string;
  pending_total_amount_minor?: number;
  total: number;
  page: number;
  page_size: number;
  pending_total_count: number;
  pending_total_amount_fen: number;
  pending_total_amount_yuan: string;
  items: WithdrawalDTO[];
}

export type WithdrawalPayoutProviderFilter =
  | 'jinshuju_red_packet'
  | 'yaoyaola_wechat_direct'
  | 'sypaymon'
  | 'alipay_direct'
  | 'csjvnpay';

export const WITHDRAWAL_PAYOUT_PROVIDER_LABELS: Record<WithdrawalPayoutProviderFilter, string> = {
  jinshuju_red_packet: '金数据',
  yaoyaola_wechat_direct: '摇摇啦',
  sypaymon: 'Sypaymon',
  alipay_direct: '支付宝商家转账',
  csjvnpay: '越南银行代付',
};

export const ALL_WITHDRAWAL_PAYOUT_PROVIDER_FILTERS: WithdrawalPayoutProviderFilter[] = [
  'jinshuju_red_packet',
  'yaoyaola_wechat_direct',
  'sypaymon',
  'alipay_direct',
];

export type WithdrawalStatus =
  | 'pending'
  | 'approved'
  | 'paying'
  | 'payout_failed'
  | 'manual_processing'
  | 'rejected'
  | 'settled';

export const WITHDRAWAL_STATUS_LABELS: Record<WithdrawalStatus, string> = {
  pending: '待审核',
  approved: '已通过待打款',
  paying: '提现中',
  payout_failed: '自动打款失败',
  manual_processing: '人工处理中',
  rejected: '已拒绝',
  settled: '已结算',
};

export const ALL_WITHDRAWAL_STATUSES: WithdrawalStatus[] = [
  'pending',
  'approved',
  'paying',
  'payout_failed',
  'manual_processing',
  'rejected',
  'settled',
];

export type WithdrawalSortOrder = 'asc' | 'desc';

export const WITHDRAWAL_SORT_ORDER_OPTIONS: { value: WithdrawalSortOrder; label: string }[] = [
  { value: 'desc', label: '申请时间降序' },
  { value: 'asc', label: '申请时间升序' },
];
